import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { AuthContext } from '../contexts/authcontext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api/axios';

// The old file registered a second axios request interceptor at module scope.
// The shared instance in api/axios.js already attaches the token.

const PASSWORD_RULES = [
  { label: '8 characters or more', test: (p) => p.length >= 8 },
  { label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'A number', test: (p) => /\d/.test(p) },
  { label: 'A special character', test: (p) => /[^A-Za-z0-9]/.test(p) }
];

const STATUS_TONE = {
  scheduled: 'sage',
  confirmed: 'sage',
  pending: 'sage',
  completed: 'muted',
  cancelled: 'clay',
  no_show: 'clay'
};

const DELETE_CONFIRM_PHRASE = 'DELETE';

const ProfilePage = () => {
  // setUser, not updateUser — the context never exposed updateUser, so the old
  // save threw "updateUser is not a function" every time.
  const { user, setUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  // Account deletion — a client-only, irreversible action, so it asks for
  // both the account password and a typed confirmation phrase before the
  // button is even clickable.
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const fetchActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      const { data } = await api.get('/profile/activity');
      setActivity(Array.isArray(data.activity) ? data.activity : []);
    } catch (error) {
      // Supplementary — a failure here should not break the page.
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
      fetchActivity();
    }
  }, [user, fetchActivity]);

  const hasProfileChanges =
    name.trim() !== (user?.name || '') || phone.trim() !== (user?.phone || '');

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (savingProfile || !hasProfileChanges) return;

    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setSavingProfile(true);
    try {
      const { data } = await api.put('/profile', {
        name: name.trim(),
        phone: phone.trim()
      });

      if (data.user) {
        const merged = { ...user, ...data.user };
        setUser(merged);
        localStorage.setItem('user', JSON.stringify(merged));
      }

      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update your profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const passwordChecks = PASSWORD_RULES.map(r => ({ ...r, passed: r.test(newPassword) }));
  const passwordValid = passwordChecks.every(c => c.passed);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (savingPassword) return;

    if (!passwordValid) {
      toast.error('Your new password does not meet all the requirements');
      return;
    }
    if (!passwordsMatch) {
      toast.error('New passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      // Goes through PUT /profile, which verifies the current password
      // server-side. The old version posted to /changepassword — the
      // registered route is /change-password, so it 404'd.
      await api.put('/profile', { currentPassword, newPassword });

      toast.success('Password updated');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not update your password');
    } finally {
      setSavingPassword(false);
    }
  };

  const deleteConfirmMatches = deleteConfirmText.trim().toUpperCase() === DELETE_CONFIRM_PHRASE;

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (deletingAccount || !deleteConfirmMatches || !deletePassword) return;

    setDeletingAccount(true);
    try {
      await api.delete('/profile', { data: { password: deletePassword } });

      toast.success('Your account has been deleted.');
      // logout() clears the token/user from storage; the account is already
      // gone server-side by this point, so this is just tidying up the
      // client. Navigate away rather than leaving them on a page that will
      // immediately start failing every request.
      await logout();
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not delete your account');
      setDeletingAccount(false);
    }
  };

  if (!user) {
    return (
      <div id="uxaah-profile">
        <ProfileStyles />
        <div className="uxp-loading"><p>Loading your profile…</p></div>
      </div>
    );
  }

  const initial = user.name ? user.name.charAt(0).toUpperCase() : 'U';

  return (
    <div id="uxaah-profile">
      <ProfileStyles />
      <ToastContainer position="top-center" />

      <div className="uxp-wrap">

        {/* ── Hero ── */}
        <header className="uxp-hero">
          <div className="uxp-hero-glow" aria-hidden="true" />
          <div className="uxp-avatar">{initial}</div>
          <div className="uxp-hero-text">
            <span className="uxp-eyebrow">Your account</span>
            <h1 className="uxp-hero-name">{user.name}</h1>
            <p className="uxp-hero-meta">{user.email}</p>
          </div>
        </header>

        <div className="uxp-grid">

          {/* ── Personal details ── */}
          <section className="uxp-card">
            <h2 className="uxp-card-title">Personal details</h2>

            <form onSubmit={handleProfileSave}>
              <label className="uxp-label" htmlFor="uxp-name">Full name</label>
              <input
                id="uxp-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="uxp-input"
                disabled={savingProfile}
              />

              <label className="uxp-label" htmlFor="uxp-phone">Phone number</label>
              <input
                id="uxp-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 90000 00000"
                className="uxp-input"
                disabled={savingProfile}
              />
              <span className="uxp-hint">
                Session reminders are sent here. Keep it current.
              </span>

              <label className="uxp-label" htmlFor="uxp-email">Email</label>
              <input
                id="uxp-email"
                type="email"
                value={user.email}
                disabled
                className="uxp-input uxp-input--locked"
              />
              <span className="uxp-hint">
                Your email is your login. Contact us if you need it changed.
              </span>

              <button
                type="submit"
                disabled={savingProfile || !hasProfileChanges}
                className="uxp-btn uxp-btn--primary uxp-btn--block"
              >
                {savingProfile ? 'Saving…' : 'Save changes'}
              </button>
            </form>
          </section>

          {/* ── Security ── */}
          <section className="uxp-card uxp-card--sage">
            <h2 className="uxp-card-title">Security</h2>
            <p className="uxp-body">
              Choose a password you do not use anywhere else. We will ask for your
              current one before making any change.
            </p>

            <ul className="uxp-facts">
              <li><span className="uxp-dot" />Sessions are end-to-end encrypted</li>
              <li><span className="uxp-dot" />Your therapist never sees your password</li>
              <li><span className="uxp-dot" />Contact details stay off public pages</li>
            </ul>

            <button
              onClick={() => setShowPasswordModal(true)}
              className="uxp-btn uxp-btn--outline uxp-btn--block"
            >
              Change password
            </button>
          </section>

          {/* ── Activity ── */}
          <section className="uxp-card uxp-card--full">
            <div className="uxp-card-head">
              <h2 className="uxp-card-title">Recent sessions</h2>
              {user.role === 'client' && (
                <Link to="/my-bookings" className="uxp-link">Manage bookings →</Link>
              )}
            </div>

            {activityLoading ? (
              <p className="uxp-muted">Loading…</p>
            ) : activity.length === 0 ? (
              <div className="uxp-empty">
                <p>No sessions yet.</p>
                {user.role === 'client' && (
                  <Link to="/doctors" className="uxp-btn uxp-btn--primary">
                    Find a therapist
                  </Link>
                )}
              </div>
            ) : (
              <ul className="uxp-list">
                {activity.map(item => (
                  <li key={`${item.type}-${item.id}`} className="uxp-list-item">
                    <div className="uxp-list-main">
                      <span className="uxp-list-name">{item.practitioner}</span>
                      <span className="uxp-muted">{item.type}</span>
                    </div>
                    <div className="uxp-list-side">
                      <span className="uxp-list-date">
                        {moment(item.appointment_time).format('D MMM YYYY')}
                      </span>
                      <span className={`uxp-pill uxp-pill--${STATUS_TONE[item.status] || 'muted'}`}>
                        {item.status}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── Danger zone — client accounts only ── */}
          {user.role === 'client' && (
            <section className="uxp-card uxp-card--danger uxp-card--full">
              <h2 className="uxp-card-title">Danger zone</h2>
              <p className="uxp-body">
                Deleting your account removes your name, email, and phone number from
                our records, and any photos you've submitted for handwriting analysis.
                Your booking history stays on file for your therapist's records and
                billing, but it will no longer be linked to you by name. This cannot
                be undone.
              </p>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="uxp-btn uxp-btn--danger"
              >
                Delete my account
              </button>
            </section>
          )}
        </div>
      </div>

      {/* ── Password modal ── */}
      {showPasswordModal && (
        <div
          className="uxp-overlay"
          onClick={() => !savingPassword && setShowPasswordModal(false)}
        >
          <div className="uxp-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="uxp-card-title">Change password</h2>

            <form onSubmit={handlePasswordSave}>
              <label className="uxp-label" htmlFor="uxp-cur">Current password</label>
              <input
                id="uxp-cur"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="uxp-input"
                autoComplete="current-password"
                disabled={savingPassword}
                required
              />

              <label className="uxp-label" htmlFor="uxp-new">New password</label>
              <input
                id="uxp-new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="uxp-input"
                autoComplete="new-password"
                disabled={savingPassword}
                required
              />

              {/* Live checklist rather than a rejection after submit. The rules
                  match signupController exactly, so nothing passes here and
                  fails server-side. */}
              {newPassword.length > 0 && (
                <ul className="uxp-checks">
                  {passwordChecks.map(c => (
                    <li key={c.label} className={c.passed ? 'is-passed' : ''}>
                      <span aria-hidden="true">{c.passed ? '✓' : '○'}</span> {c.label}
                    </li>
                  ))}
                </ul>
              )}

              <label className="uxp-label" htmlFor="uxp-conf">Confirm new password</label>
              <input
                id="uxp-conf"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`uxp-input ${confirmPassword && !passwordsMatch ? 'uxp-input--error' : ''}`}
                autoComplete="new-password"
                disabled={savingPassword}
                required
              />
              {confirmPassword && !passwordsMatch && (
                <span className="uxp-hint uxp-hint--error">Passwords do not match</span>
              )}

              <div className="uxp-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={savingPassword}
                  className="uxp-btn uxp-btn--outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPassword || !passwordValid || !passwordsMatch}
                  className="uxp-btn uxp-btn--primary"
                >
                  {savingPassword ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete account modal ── */}
      {showDeleteModal && (
        <div
          className="uxp-overlay"
          onClick={() => !deletingAccount && setShowDeleteModal(false)}
        >
          <div className="uxp-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="uxp-card-title">Delete your account</h2>
            <p className="uxp-body">
              This is permanent. Your name, email, phone number, and any handwriting
              samples will be removed. Type <strong>{DELETE_CONFIRM_PHRASE}</strong> and
              enter your password to confirm.
            </p>

            <form onSubmit={handleDeleteAccount}>
              <label className="uxp-label" htmlFor="uxp-del-confirm">
                Type {DELETE_CONFIRM_PHRASE} to confirm
              </label>
              <input
                id="uxp-del-confirm"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="uxp-input"
                disabled={deletingAccount}
                autoComplete="off"
                required
              />

              <label className="uxp-label" htmlFor="uxp-del-password">Password</label>
              <input
                id="uxp-del-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                className="uxp-input"
                autoComplete="current-password"
                disabled={deletingAccount}
                required
              />

              <div className="uxp-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deletingAccount}
                  className="uxp-btn uxp-btn--outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={deletingAccount || !deleteConfirmMatches || !deletePassword}
                  className="uxp-btn uxp-btn--danger"
                >
                  {deletingAccount ? 'Deleting…' : 'Delete my account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* Everything is scoped under #uxaah-profile so global rules in style.css —
   which set colours on bare h1/p/input — cannot bleed in and, in the hero's
   case, render dark navy text on a dark navy background. */
const ProfileStyles = () => (
  <style>{`
    #uxaah-profile {
      --uxp-navy:      #1a2344;
      --uxp-blue:      #303c6c;
      --uxp-blue-lite: #4a579d;
      --uxp-orange:    #e8622a;
      --uxp-sage:      #4a7c6f;
      --uxp-sage-lite: #e8f2ef;
      --uxp-clay:      #c0392b;
      --uxp-clay-lite: #fdecea;
      --uxp-ink:       #111827;
      --uxp-body:      #374151;
      --uxp-muted:     #6b7280;
      --uxp-line:      rgba(48,60,108,0.10);
      --uxp-sh-sm:     0 4px 12px rgba(48,60,108,0.05);
      --uxp-sh-md:     0 12px 32px rgba(48,60,108,0.09);
      --uxp-sh-lg:     0 24px 60px rgba(48,60,108,0.13);

      font-family: 'DM Sans', system-ui, sans-serif;
      color: var(--uxp-ink);
      background: #f9fafc;
      min-height: 100vh;
      padding: 40px 0 80px;
    }

    #uxaah-profile *, #uxaah-profile *::before, #uxaah-profile *::after { box-sizing: border-box; }

    #uxaah-profile .uxp-wrap { max-width: 920px; margin: 0 auto; padding: 0 24px; }
    #uxaah-profile .uxp-loading { text-align: center; padding: 100px 24px; color: var(--uxp-muted); }

    /* ── Hero ── */
    #uxaah-profile .uxp-hero {
      position: relative; overflow: hidden;
      display: flex; align-items: center; gap: 24px;
      padding: 36px 40px; margin-bottom: 28px; border-radius: 28px;
      background: linear-gradient(135deg, #1a2344 0%, #303c6c 55%, #3d4a86 100%);
      box-shadow: var(--uxp-sh-lg);
    }
    #uxaah-profile .uxp-hero-glow {
      position: absolute; top: -60%; right: -10%;
      width: 380px; height: 380px; border-radius: 50%;
      background: radial-gradient(circle, rgba(232,98,42,0.35), transparent 65%);
      pointer-events: none;
    }
    #uxaah-profile .uxp-avatar {
      position: relative; z-index: 1; flex-shrink: 0;
      width: 84px; height: 84px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-family: 'DM Serif Display', Georgia, serif; font-size: 34px;
      color: #ffffff;
      background: rgba(255,255,255,0.14);
      border: 1.5px solid rgba(255,255,255,0.3);
      backdrop-filter: blur(6px);
    }
    #uxaah-profile .uxp-hero-text { position: relative; z-index: 1; min-width: 0; }
    #uxaah-profile .uxp-eyebrow {
      display: block; font-size: 11.5px; font-weight: 700;
      letter-spacing: 0.16em; text-transform: uppercase;
      color: rgba(255,255,255,0.55); margin-bottom: 6px;
    }
    #uxaah-profile .uxp-hero-name {
      margin: 0; font-family: 'DM Serif Display', Georgia, serif;
      font-size: clamp(26px, 4vw, 34px); line-height: 1.15;
      color: #ffffff; letter-spacing: -0.01em;
      overflow-wrap: anywhere;
    }
    #uxaah-profile .uxp-hero-meta {
      margin: 6px 0 0; font-size: 14.5px;
      color: rgba(255,255,255,0.72); overflow-wrap: anywhere;
    }

    /* ── Cards ── */
    #uxaah-profile .uxp-grid {
      display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 20px;
    }
    #uxaah-profile .uxp-card {
      background: #ffffff; border-radius: 24px; padding: 32px;
      border: 1px solid rgba(48,60,108,0.07);
      box-shadow: var(--uxp-sh-sm);
      transition: box-shadow 0.3s ease, transform 0.3s ease;
    }
    #uxaah-profile .uxp-card:hover { box-shadow: var(--uxp-sh-md); }
    #uxaah-profile .uxp-card--full { grid-column: 1 / -1; }
    #uxaah-profile .uxp-card--sage {
      background: linear-gradient(160deg, var(--uxp-sage-lite) 0%, #ffffff 70%);
    }
    #uxaah-profile .uxp-card--danger {
      background: linear-gradient(160deg, var(--uxp-clay-lite) 0%, #ffffff 70%);
      border-color: rgba(192,57,43,0.15);
    }
    #uxaah-profile .uxp-card-head {
      display: flex; justify-content: space-between; align-items: baseline;
      flex-wrap: wrap; gap: 10px;
    }
    #uxaah-profile .uxp-card-title {
      font-family: 'DM Serif Display', Georgia, serif;
      font-size: 21px; color: var(--uxp-navy); margin: 0 0 18px; font-weight: 400;
    }
    #uxaah-profile .uxp-card--danger .uxp-card-title { color: var(--uxp-clay); }
    #uxaah-profile .uxp-body {
      color: var(--uxp-body); font-size: 14.5px; line-height: 1.65; margin: 0 0 20px;
    }

    /* ── Trust facts ── */
    #uxaah-profile .uxp-facts { list-style: none; padding: 0; margin: 0 0 26px; display: grid; gap: 11px; }
    #uxaah-profile .uxp-facts li {
      display: flex; align-items: center; gap: 10px;
      font-size: 13.5px; color: var(--uxp-body);
    }
    #uxaah-profile .uxp-dot {
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--uxp-sage); flex-shrink: 0;
    }

    /* ── Form ── */
    #uxaah-profile .uxp-label {
      display: block; font-size: 12.5px; font-weight: 700;
      letter-spacing: 0.03em; color: var(--uxp-body);
      margin: 20px 0 7px;
    }
    #uxaah-profile .uxp-label:first-of-type { margin-top: 0; }
    #uxaah-profile .uxp-input {
      width: 100%; padding: 13px 16px; border-radius: 12px;
      border: 1.5px solid var(--uxp-line);
      font-family: inherit; font-size: 14.5px; color: var(--uxp-ink);
      background: #ffffff; outline: none;
      transition: border-color 0.25s ease, box-shadow 0.25s ease;
    }
    #uxaah-profile .uxp-input::placeholder { color: #b6bcc7; }
    #uxaah-profile .uxp-input:focus {
      border-color: var(--uxp-blue);
      box-shadow: 0 0 0 4px rgba(48,60,108,0.09);
    }
    #uxaah-profile .uxp-input--locked {
      background: #f6f7fa; color: var(--uxp-muted); cursor: not-allowed;
    }
    #uxaah-profile .uxp-input--error { border-color: var(--uxp-clay); }
    #uxaah-profile .uxp-input:disabled { opacity: 0.7; }

    #uxaah-profile .uxp-hint {
      display: block; font-size: 12px; color: var(--uxp-muted); margin-top: 7px; line-height: 1.5;
    }
    #uxaah-profile .uxp-hint--error { color: var(--uxp-clay); }

    /* ── Password checklist ── */
    #uxaah-profile .uxp-checks {
      list-style: none; padding: 14px 16px; margin: 12px 0 0;
      background: #f9fafc; border-radius: 12px; display: grid; gap: 6px;
    }
    #uxaah-profile .uxp-checks li {
      font-size: 12.5px; color: #9ca3af; transition: color 0.25s ease;
    }
    #uxaah-profile .uxp-checks li.is-passed { color: var(--uxp-sage); font-weight: 600; }

    /* ── Buttons ── */
    #uxaah-profile .uxp-btn {
      display: inline-flex; align-items: center; justify-content: center;
      padding: 13px 28px; border-radius: 50px; border: none;
      font-family: inherit; font-size: 14.5px; font-weight: 600;
      cursor: pointer; text-decoration: none;
      transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
    }
    #uxaah-profile .uxp-btn--block { width: 100%; margin-top: 26px; }
    #uxaah-profile .uxp-btn--primary {
      background: var(--uxp-navy); color: #ffffff;
      box-shadow: 0 6px 18px rgba(26,35,68,0.22);
    }
    #uxaah-profile .uxp-btn--primary:hover:not(:disabled) {
      background: var(--uxp-blue); transform: translateY(-2px);
      box-shadow: 0 10px 26px rgba(26,35,68,0.30);
    }
    #uxaah-profile .uxp-btn--outline {
      background: #ffffff; color: var(--uxp-body);
      border: 1.5px solid var(--uxp-line);
    }
    #uxaah-profile .uxp-btn--outline:hover:not(:disabled) {
      border-color: var(--uxp-blue); color: var(--uxp-blue); transform: translateY(-2px);
    }
    #uxaah-profile .uxp-btn--danger {
      background: var(--uxp-clay); color: #ffffff;
      box-shadow: 0 6px 18px rgba(192,57,43,0.22);
    }
    #uxaah-profile .uxp-btn--danger:hover:not(:disabled) {
      background: #a5322a; transform: translateY(-2px);
      box-shadow: 0 10px 26px rgba(192,57,43,0.30);
    }
    #uxaah-profile .uxp-btn:disabled { opacity: 0.45; cursor: default; transform: none; box-shadow: none; }

    #uxaah-profile .uxp-link {
      color: var(--uxp-orange); text-decoration: none;
      font-weight: 600; font-size: 13.5px;
      transition: opacity 0.2s ease;
    }
    #uxaah-profile .uxp-link:hover { opacity: 0.75; }

    /* ── Activity list ── */
    #uxaah-profile .uxp-list { list-style: none; padding: 0; margin: 0; }
    #uxaah-profile .uxp-list-item {
      display: flex; justify-content: space-between; align-items: center;
      gap: 16px; padding: 16px 0;
      border-bottom: 1px solid rgba(48,60,108,0.06);
    }
    #uxaah-profile .uxp-list-item:last-child { border-bottom: none; padding-bottom: 0; }
    #uxaah-profile .uxp-list-main { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
    #uxaah-profile .uxp-list-side {
      display: flex; flex-direction: column; align-items: flex-end; gap: 6px; flex-shrink: 0;
    }
    #uxaah-profile .uxp-list-name {
      font-size: 15.5px; font-weight: 600; color: var(--uxp-navy); overflow-wrap: anywhere;
    }
    #uxaah-profile .uxp-list-date { font-size: 13.5px; color: var(--uxp-body); font-weight: 500; }
    #uxaah-profile .uxp-muted { font-size: 13px; color: var(--uxp-muted); }

    #uxaah-profile .uxp-pill {
      font-size: 10.5px; font-weight: 700; letter-spacing: 0.07em;
      text-transform: uppercase; padding: 4px 11px; border-radius: 50px;
    }
    #uxaah-profile .uxp-pill--sage  { background: rgba(74,124,111,0.13); color: var(--uxp-sage); }
    #uxaah-profile .uxp-pill--clay  { background: rgba(192,57,43,0.10);  color: var(--uxp-clay); }
    #uxaah-profile .uxp-pill--muted { background: rgba(48,60,108,0.07);  color: var(--uxp-muted); }

    #uxaah-profile .uxp-empty {
      text-align: center; padding: 36px 20px;
      background: #fafbfc; border-radius: 18px; color: var(--uxp-muted);
    }
    #uxaah-profile .uxp-empty p { margin: 0 0 18px; }

    /* ── Modal ── */
    #uxaah-profile .uxp-overlay {
      position: fixed; inset: 0; z-index: 1000;
      background: rgba(26,35,68,0.55);
      backdrop-filter: blur(4px);
      display: flex; align-items: center; justify-content: center; padding: 24px;
      animation: uxpFade 0.2s ease;
    }
    #uxaah-profile .uxp-modal {
      background: #ffffff; border-radius: 24px; padding: 36px;
      max-width: 460px; width: 100%; max-height: 86vh; overflow-y: auto;
      box-shadow: var(--uxp-sh-lg);
      animation: uxpRise 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
    }
    #uxaah-profile .uxp-modal-actions { display: flex; gap: 12px; margin-top: 28px; }
    #uxaah-profile .uxp-modal-actions .uxp-btn { flex: 1; }

    @keyframes uxpFade { from { opacity: 0; } to { opacity: 1; } }
    @keyframes uxpRise {
      from { opacity: 0; transform: translateY(16px) scale(0.98); }
      to   { opacity: 1; transform: none; }
    }

    @media (max-width: 620px) {
      #uxaah-profile { padding: 24px 0 60px; }
      #uxaah-profile .uxp-wrap { padding: 0 16px; }
      #uxaah-profile .uxp-hero {
        flex-direction: column; align-items: flex-start;
        gap: 18px; padding: 28px 24px; border-radius: 22px;
      }
      #uxaah-profile .uxp-card { padding: 24px; border-radius: 20px; }
      #uxaah-profile .uxp-list-item { flex-direction: column; align-items: flex-start; gap: 8px; }
      #uxaah-profile .uxp-list-side { align-items: flex-start; }
      #uxaah-profile .uxp-modal { padding: 26px; }
      #uxaah-profile .uxp-modal-actions { flex-direction: column-reverse; }
    }
  `}</style>
);

export default ProfilePage;